import type { CSSProperties } from 'react';
import type { TFunction } from 'i18next';
import type { ClientDocumentDownload, ClientProjectDocument } from '@/types/project-documents';

/** Category labels of shared documents (same keys as the provider's Documents tab). */
const CATEGORY_LABELS: Record<string, string> = {
  contract: 'Contract',
  proposal: 'Proposal or quote',
  requirements: 'Requirements or brief',
  change_request: 'Change request',
  design: 'Design',
  client_content: 'Content from you',
  credentials: 'Access document',
  invoice: 'Invoice or receipt',
  report: 'Report',
  handover: 'Handover',
  other: 'Document',
};

export const documentCategoryLabel = (t: TFunction, category: string): string =>
  t(`documents.categories.${category}`, { defaultValue: CATEGORY_LABELS[category] ?? CATEGORY_LABELS.other });

export const formatFileSize = (bytes: number): string | null => {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
};

/** "spec.pdf · 1.2 MB", "Link", or "File no longer available". */
export const documentFileLabel = (t: TFunction, doc: Pick<ClientProjectDocument, 'is_link' | 'file_name' | 'size' | 'available'>): string => {
  if (!doc.available) return t('documents.unavailable', { defaultValue: 'File no longer available' });
  if (doc.is_link) return t('documents.link', { defaultValue: 'Link' });
  const size = formatFileSize(doc.size);
  return [doc.file_name, size].filter(Boolean).join(' · ');
};

export const approvalTagColor = (status: string): string =>
  status === 'approved' ? 'success' : status === 'rejected' ? 'error' : status === 'pending' ? 'gold' : 'default';

/** Only web links are opened (defence in depth: a javascript: or data: URL from the API is refused). */
export const isSafeDownloadUrl = (value: string): boolean => {
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

/** Opens a link in a new tab, or starts the download of a short-lived file link. False when the URL is refused. */
export const openDownload = (target: ClientDocumentDownload): boolean => {
  if (!isSafeDownloadUrl(target.url)) return false;
  const anchor = document.createElement('a');
  anchor.href = target.url;
  anchor.rel = 'noopener noreferrer';
  if (target.kind === 'link') anchor.target = '_blank';
  else anchor.download = target.name ?? '';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return true;
};

/** Hidden on screen, read by screen readers (table header of the actions column). */
export const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
